/**
 * @module app.core
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IApiQueryResults, IQueryLoadOptions, IApiUploadRequest, IApiUploadResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import { IDocumentsConfig } from "app/core/models/essette-interfaces";
import * as Core from 'app/core/models/essette-api';


const defaultConfig: IDocumentsConfig = {
    serviceRootUrl: ""  // root of "Core" queries. Should not use this one because it lacks proper context for some of its actions
}

@transient()
@autoinject()
export class DocumentsService {

    ////****************** Fields and Properties ************************************////
    private isConfigured: boolean = false;
    private config: IDocumentsConfig;
    private urlPathStart: string = '';


    ////****************** Functions ************************************************////
    constructor(protected readonly api: Api) {
        this.config = defaultConfig;
    }

    public configure(config: IDocumentsConfig): void {
        this.config = { ...{}, ...defaultConfig, ...config };
        this.isConfigured = true;
        this.urlPathStart = this.config.targetType == 'FM.FaxDocument' ? '' : 'documents';
    }

    private checkConfiguration(): void {
        if(!this.isConfigured) {
            throw Error("Documents service requires module-specific configuration! Use configure() method.");
        }
    }


    ////****************** Queries **************************************************////
    public async loadDocumentsByTarget(targetId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.Document>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}/${targetId}/documents`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(this.config.categoryFlagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.categoryFlagCodes || []));
        }

        return await this.api.get(query);
    }

    public async loadCategories(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.DocumentCategory>> {
        this.checkConfiguration();

        const query = this.api
            .query(`${this.config.serviceRootUrl}${this.urlPathStart.length ? '/' + this.urlPathStart : ''}/categories`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(this.config.categoryFlagCodes) {
            query.filterExists('flags', q => q.filterStringArray('code', this.config.categoryFlagCodes || []));
        }

        return await this.api.get(query);
    }

    public async loadCategoryById(categoryId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.DocumentCategory>> {
        this.checkConfiguration();
        const query = this.api
            .query(`${this.config.serviceRootUrl}${this.urlPathStart.length ? '/' + this.urlPathStart : ''}/categories/${categoryId}`)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadDocumentFlags(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.DocumentFlag>> {
        this.checkConfiguration();

        const query = this.api
            .query(`${this.config.serviceRootUrl}/documents/flags`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    ////****************** Loads ****************************************************////


    ////****************** Actions **************************************************////
    public async uploadFile(request: IApiUploadRequest): Promise<IApiUploadResults> {
        this.checkConfiguration();

        const action = this.api
            .action('upload', `${this.config.serviceRootUrl}/${this.urlPathStart}`, request);
        return await this.api.post(action);
    }

    public async downloadFile(documentId: string): Promise<Blob> {
        this.checkConfiguration();
        const request = this.api
            .download(`${this.config.serviceRootUrl}/documents/${documentId}`);
        return await this.api.get(request);
    }

    public async downloadRollupFile(documentId: string): Promise<Blob> {
        this.checkConfiguration();
        const request = this.api
            .download(`${this.config.serviceRootUrl}/rollup/documents/${documentId}`);
        return await this.api.get(request);
    }

    public async attachDocument(document: Core.Document): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api
            .action('attach', `${this.config.serviceRootUrl}/documents`, document);
        return await this.api.post(action);
    }

    public async updateDocument(document: Core.Document): Promise<IApiActionResults> {
        this.checkConfiguration();
        const action = this.api
            .action('update', `${this.config.serviceRootUrl}/${document.id}`, document);
        return await this.api.post(action);
    }

    public async activateAttachment(attachmentId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api
            .action(actionCode, `${this.config.serviceRootUrl}/documents/attachments/${attachmentId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        }
        else {
            return await this.api.delete(action);
        }
    }


    public async activateDocumentCategory(documentCategoryId: string, isActive: boolean): Promise<IApiActionResults> {
        this.checkConfiguration()

        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `${this.config.serviceRootUrl}/documents/categories/${documentCategoryId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }


    public async saveDocumentCategory(documentCategory: Core.DocumentCategory): Promise<IApiActionResults> {
        this.checkConfiguration()

        const actionName = !documentCategory.id ? 'insert' : 'update';
        const url = `${this.config.serviceRootUrl}/documents/categories/${documentCategory.id ? `${documentCategory.id}` : ``}`;
        const action = this.api.action(actionName, url, documentCategory);
        return await this.api.post(action);
    }

}
